/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import {
  KPI_GRANULARITY_HOURLY,
  KPI_GRANULARITY_DAILY,
  KPI_GRANULARITY_MONTHLY,
  KPI_GRANULARITY_WEEKLY,
  KPI_GRANULARITY_YEARLY,
  RESOURCE_TYPES,
  DATAITEM_TYPE_DIMENSION,
  DATAITEM_TYPE_DERIVED_DIMENSION,
} from 'constants';

import { defineMessages } from 'react-intl';
import isNil from 'lodash/isNil';

import { getKPIDetails } from './KPIAPI';

import {
  determineTimeFrequency,
  determineDataItemType,
  isDataItemCalculated,
  isDataItemDerived,
  isDataItemRawMetric,
} from 'dataFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet } from 'FetchAPIs';
import { getGranularity } from 'GranularityAPI';
import {
  stripDevicesFromPath,
  stripSystemChunkFromLocationHierarchyPath,
  getDeviceIdFromHierarchyPath,
} from 'hierarchyUtils';

const messages = defineMessages({
  errorData: 'Error loading data items.',
  errorDataGraph: 'Error loading data item graph.',
  errorRecentData: 'Error loading recent data',
});

const baseHierarchiesURL = '/api/hierarchies';
const baseDatalakeDataURL = '/api/datalake/data';

export function getDataItemsAPI(hierarchyPath, includeHierarchyInResults = false) {
  return fetchGet(
    `${baseHierarchiesURL}${stripDevicesFromPath(
      stripSystemChunkFromLocationHierarchyPath(
        stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
      )
    )}${includeHierarchyInResults ? '/hierarchy' : ''}/dataItems`
  )
    .then(response => ({
      data: Array.isArray(response)
        ? response.map(dataItem => ({
            ...dataItem,
            // type: determineDataItemType(dataItem),
            subDataItems: dataItem.subDataItems ? dataItem.subDataItems : undefined,
          }))
        : [],
    }))
    .catch(error => {
      throw new I18NError(messages.errorData, error);
    });
}

/**
 *
 * @param {string} hierarchyPath the path to the resource (exemple: /deviceType/{deviceTypeId})
 * @param {object} dataItem full data item object so we can determine whether to make the extra fetch for kpi information
 * @returns object DataItemDto populated with kpiFunctionDto information
 */
export function getDataItemDetailsAPI(hierarchyPath, dataItem) {
  // Only have to make these additional request for derived and calculated items
  if (isDataItemCalculated(dataItem) || isDataItemDerived(dataItem)) {
    // Currently the only KPI Details are the KPI Details
    return getKPIDetails(hierarchyPath, dataItem.uuid).then(dataItemResponse => {
      /* eslint-disable no-param-reassign */
      dataItemResponse.type = determineDataItemType(dataItemResponse);
      // Need to also load the granularity so I can understand the timefrequency to query on
      if (dataItemResponse.kpiFunctionDto) {
        const {
          kpiFunctionDto: { granularity },
        } = dataItemResponse;
        // Create another dataItem object to avoid breaking redux state immutability
        // Also, make sure catalogFunctionName is copied to functionName, so that there is no need to replace every occurrence of functioName with catalogFunctionName in the code
        // TODO: Check with backend team if it is possible to rollback to functionName instead of catalogFunctionName. Other places in the code base might break after this name change
        // Another backend change: granularity is now granularityName
        dataItemResponse = {
          ...dataItemResponse,
          kpiFunctionDto: {
            ...dataItemResponse.kpiFunctionDto,
            functionName:
              dataItemResponse.kpiFunctionDto.catalogFunctionName ||
              dataItemResponse.kpiFunctionDto.functionName,
            granularity:
              dataItemResponse.kpiFunctionDto.granularityName ||
              dataItemResponse.kpiFunctionDto.granularity,
          },
        };
        // Go get the full granularity
        if (granularity) {
          return getGranularity(hierarchyPath, granularity).then(granularities => {
            // Load full granularity object
            dataItemResponse.kpiFunctionDto.granularity =
              granularities.length > 0 ? granularities[0] : { name: granularity };

            return dataItemResponse;
          });
        }
        return dataItemResponse;
      }
      return dataItemResponse;
    });
    /* eslint-enable no-param-reassign */
  }
  return Promise.resolve(dataItem);
}

// TODO: Instead of a switch statement, the real api urls should use the
// dataItem.type, which would then return the correct format of data for the
// graph it's powering.
export function getDataItemGraph(hierarchyPath, dataItem, selectedFrequency, hierarchyItemType) {
  let bucketGranularity;
  let dataRange;
  const frequency = selectedFrequency || determineTimeFrequency(dataItem);
  const deviceId = getDeviceIdFromHierarchyPath(hierarchyPath);
  let queryParams = { dataItemName: dataItem.name };
  // If this is a device hierarchy path, include the deviceId filter
  if (deviceId) {
    queryParams.deviceIds = deviceId;
  }

  switch (dataItem.type) {
    case DATAITEM_TYPE_DIMENSION:
      return fetchGet(
        `${baseDatalakeDataURL}${stripDevicesFromPath(
          stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
        )}/devices/dimension/count`,
        null,
        queryParams
      ).then(response => ({ data: response }));
    case DATAITEM_TYPE_DERIVED_DIMENSION:
      queryParams.limit = 200;
      return fetchGet(
        `${baseDatalakeDataURL}${stripDevicesFromPath(
          stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
        )}/functionResults`,
        null,
        queryParams
      ).then(response => ({ data: response }));

    default:
      switch (frequency) {
        case KPI_GRANULARITY_HOURLY:
          bucketGranularity = 'HOUR';
          dataRange = 'LAST_24H';
          break;
        case KPI_GRANULARITY_WEEKLY:
          bucketGranularity = 'WEEK';
          dataRange = 'LAST_3M';
          break;
        case KPI_GRANULARITY_MONTHLY:
          bucketGranularity = 'MONTH';
          dataRange = 'LAST_12M';
          break;
        case KPI_GRANULARITY_YEARLY:
          bucketGranularity = 'YEAR';
          dataRange = 'LAST_5Y';
          break;
        case KPI_GRANULARITY_DAILY:
        default:
          bucketGranularity = 'DAY';
          dataRange = 'LAST_30D';
          break;
      }

      queryParams = {
        ...queryParams,
        type: bucketGranularity,
        range: dataRange,
        groupByDeviceId: hierarchyItemType !== RESOURCE_TYPES.DEVICE_TYPE,
      };

      return fetchGet(
        `${baseDatalakeDataURL}${stripDevicesFromPath(
          stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
        )}/aggregate`,
        null,
        queryParams
      )
        .then(response => ({ data: response }))
        .catch(error => {
          throw new I18NError(messages.errorDataGraph, error);
        });
  }
}

/**
 *
 * @param {string} hierarchyPath the path to the resource (exemple: /deviceType/{deviceTypeId})
 * @param {*} dataItem, the specific dataItem to return data for
 */
export function getDataItemRecentData(hierarchyPath, dataItem) {
  const deviceId = getDeviceIdFromHierarchyPath(hierarchyPath);
  const isRawMetric = isDataItemRawMetric(dataItem);
  const recentDataEndpointURL = isRawMetric
    ? `${baseDatalakeDataURL}${stripDevicesFromPath(
        stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
      )}/raw`
    : `${baseDatalakeDataURL}${stripDevicesFromPath(
        stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
      )}/functionResults`;

  const queryParams = {
    limit: 200,
  };
  if (isRawMetric) {
    queryParams.dataItems = dataItem?.name;
  } else {
    queryParams.dataItemName = dataItem?.name;
  }

  // If this is a device hierarchy path, include the deviceId filter
  if (deviceId) {
    queryParams.deviceIds = deviceId;
  }

  switch (dataItem.type) {
    case DATAITEM_TYPE_DIMENSION:
    case DATAITEM_TYPE_DERIVED_DIMENSION:
      // There is no recent data for dimension data items.
      // Calling the /functionResults API on a data item of type dimension results in a 400 error.
      return Promise.resolve([]);

    default:
      return fetchGet(recentDataEndpointURL, null, queryParams)
        .then(response => ({
          data: Array.isArray(response)
            ? response
                .filter(data => !isNil(data))
                // v1 APIs used to return an id property, so copy entity_id into id to avoid multiple changes across the code base
                // but it can also be just form the id, so it will be the fallback here
                .map(d => ({ ...d, id: d?.entity_id ?? d.id }))
            : [],
        }))
        .catch(error => {
          throw new I18NError(messages.errorRecentData, error);
        });
  }
}
